import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./manageBlog.module.scss";
import { connect, useDispatch } from "react-redux";
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PreviewBlog from "./PreviewBlog";
import RichTextEditor from "./richTextEditor/RichTextEditor";
import {
  sectionsToTiptapJSON,
  tiptapJSONToSections,
} from "./richTextEditor/blogSerializer";
import { ClipLoader } from "react-spinners";
import { KeyboardArrowLeftOutlined } from "@mui/icons-material";
import CropImageUpload from "./CropImageUpload";
import RelatedProductsSearch from "../relatedProductsSearch/RelatedProductsSearch";
import AIPromptDialog from "./AIPromptDialog";
import {
  createBlogs,
  getActiveBlogCategory,
  getAllProduct,
  getBlogsByIdResponse,
  updateBlogs,
} from "../../../store/actions";
import { useS3Upload } from "../../../customHooks/S3Upload";
import { DESTINATIONS } from "../../../utils/common/constants";

const DEFAULT_ASPECT = 1.91;
const MAIN_CROP_SIZE = {
  width: 1600,
  height: Math.round(1600 / DEFAULT_ASPECT),
};
const OG_SIZES = [
  { w: 1200, h: 630 },
  { w: 1200, h: 627 },
  { w: 800, h: 418 },
  { w: 600, h: 335 },
];

const defaultBlog = {
  status: 1,
  blogCategoryIds: [],
  title: "",
  miniDescription: "",
  imagePreviewDataUrl: "",
  imageUrl: "",
  imageFile: null,
  originalImageUrl: "",
  relatedProductIds: [],
  sections: [],
  label: "",
};

const extractS3KeyFromUrl = (url) => {
  if (!url || typeof url !== "string" || url.startsWith("data:")) return null;
  try {
    const withoutQuery = url.split("?")[0];
    const segments = withoutQuery.split("/").filter(Boolean);
    return segments.length ? segments[segments.length - 1] : null;
  } catch (error) {
    console.error("Unable to parse S3 key from url", error);
    return null;
  }
};

const srcToImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("Image source missing"));
      return;
    }
    const img = new Image();
    if (typeof src === "string" && !src.startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const ManageBlog = ({
  products = [],
  activeBlogCategories = [],
  blogFromState = {},
}) => {
  const dispatch = useDispatch();
  const [, uploadToS3] = useS3Upload();
  const [blog, setBlog] = useState({ ...defaultBlog });
  const [tiptapContent, setTiptapContent] = useState(null);
  const [errors, setErrors] = useState({});
  const [ogPanelOpen, setOgPanelOpen] = useState(false);
  const [ogImages, setOgImages] = useState([]);
  const [relatedProductsOpen, setRelatedProductsOpen] = useState(false);
  const uploadInFlight = useRef(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1400
  );
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [aiPromptOpen, setAiPromptOpen] = useState(false);

  const selectedBlogCategories = useMemo(() => {
    if (
      !Array.isArray(activeBlogCategories) ||
      activeBlogCategories.length === 0
    ) {
      return [];
    }
    return blog.blogCategoryIds
      .map((id) => activeBlogCategories.find((cat) => cat?.id === id))
      .filter(Boolean);
  }, [activeBlogCategories, blog.blogCategoryIds]);

  const categoryPreviewLabel = useMemo(() => {
    if (!selectedBlogCategories.length) return "";
    if (selectedBlogCategories.length === 1) {
      return selectedBlogCategories[0]?.name || "";
    }
    const [first, ...rest] = selectedBlogCategories;
    return `${first?.name ?? ""} +${rest.length}`;
  }, [selectedBlogCategories]);

  const selectedRelatedProducts = useMemo(() => {
    if (!Array.isArray(products) || products.length === 0) return [];
    return blog.relatedProductIds
      .map((id) => products.find((p) => p?.id === id))
      .filter(Boolean);
  }, [blog.relatedProductIds, products]);

  useEffect(() => {
    dispatch(getAllProduct());
    dispatch(getActiveBlogCategory());
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      const narrow = window.innerWidth < 1400;
      setIsNarrow(narrow);
      setSidebarOpen(!narrow);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!blogFromState || Object.keys(blogFromState).length === 0) {
      setBlog({ ...defaultBlog });
      setOgImages([]);
      setTiptapContent(null);
      return;
    }

    const statusValue =
      typeof blogFromState.status === "number"
        ? blogFromState.status
        : blogFromState.status
        ? 1
        : 0;

    const categoryIds = Array.isArray(blogFromState.blogCategoryId)
      ? blogFromState.blogCategoryId
      : blogFromState.blogCategoryId
      ? [blogFromState.blogCategoryId]
      : [];

    const relatedProducts = Array.isArray(blogFromState.productId)
      ? blogFromState.productId
      : blogFromState.productId
      ? [blogFromState.productId]
      : [];

    const sectionsFromState = Array.isArray(blogFromState.sections)
      ? blogFromState.sections.map((section, idx) => {
          const base = {
            ...section,
            id: section?.id || `loaded-${idx}`,
            file: null,
            previewDataUrl: "",
          };

          if (section?.type === "image") {
            return {
              ...base,
              originalImageUrl: section?.imageUrl || "",
            };
          }

          if (section?.type === "table") {
            return {
              ...base,
              rows: Array.isArray(section?.rows)
                ? section.rows.map((row) =>
                    Array.isArray(row) ? [...row] : row
                  )
                : [["", ""]],
            };
          }

          if (section?.type === "list") {
            const normalizedItems = Array.isArray(section?.items)
              ? section.items.map((item, itemIdx) => ({
                  id: item?.id || `${base.id}-item-${itemIdx}`,
                  text: item?.text || "",
                  level: Math.max(0, Number(item?.level) || 0),
                }))
              : [{ id: `${base.id}-item-0`, text: "", level: 0 }];

            return {
              ...base,
              listStyle: section?.listStyle || "bullet",
              listStyles: Array.isArray(section?.listStyles)
                ? section.listStyles
                : [section?.listStyle || "bullet"],
              items: normalizedItems,
            };
          }

          return base;
        })
      : [];

    setBlog({
      ...defaultBlog,
      id: blogFromState.id,
      status: statusValue,
      blogCategoryIds: categoryIds,
      title: blogFromState.title || "",
      label: blogFromState?.label || "",
      miniDescription: blogFromState.miniDescription || "",
      imageUrl: blogFromState.imageUrl || "",
      originalImageUrl: blogFromState.imageUrl || "",
      relatedProductIds: relatedProducts,
      sections: sectionsFromState,
    });

    setTiptapContent(sectionsToTiptapJSON(sectionsFromState));

    if (
      Array.isArray(blogFromState.ogPreviews) &&
      blogFromState.ogPreviews.length
    ) {
      setOgImages(
        blogFromState.ogPreviews.map(({ size, imageUrl }) => ({
          size,
          imageUrl,
          dataUrl: "",
          originalImageUrl: imageUrl,
        }))
      );
    } else {
      setOgImages([]);
    }
  }, [blogFromState]);

  const handleAddRelatedProducts = () => {
    setRelatedProductsOpen(true);
  };

  const toggleRelatedProduct = (productId) => {
    if (!productId) return;
    setBlog((prev) => {
      const exists = prev.relatedProductIds.includes(productId);
      const updatedIds = exists
        ? prev.relatedProductIds.filter((id) => id !== productId)
        : [...prev.relatedProductIds, productId];
      return { ...prev, relatedProductIds: updatedIds };
    });
  };

  const handleRelatedProductsSave = () => {
    setRelatedProductsOpen(false);
  };

  const beginUpload = () => {
    uploadInFlight.current += 1;
    setIsUploading(true);
  };

  const finishUpload = () => {
    uploadInFlight.current = Math.max(0, uploadInFlight.current - 1);
    if (uploadInFlight.current === 0) {
      setIsUploading(false);
    }
  };

  const isBusy = isUploading || isSubmitting;

  const validate = () => {
    const e = {};
    if (!blog.blogCategoryIds || blog.blogCategoryIds.length === 0) {
      e.blogCategory = "Blog category is required.";
    }
    if (!blog.title.trim()) e.title = "Title is required.";
    if (!blog.miniDescription.trim()) {
      e.miniDescription = "Mini description is required.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (isBusy) return;
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      let mainImageUrl = blog.imageUrl;
      if (!mainImageUrl && blog.imageFile) {
        beginUpload();
        try {
          const existingMainKey = blog.id
            ? extractS3KeyFromUrl(
                blogFromState?.imageUrl ||
                  blog.originalImageUrl ||
                  blog.imageUrl
              )
            : null;
          const uploadKey = existingMainKey || "blogMainImage";
          const uploadedUrl = await uploadToS3(
            uploadKey,
            blog.imageFile,
            DESTINATIONS?.BLOG_DETAILS,
            false
          );
          if (!uploadedUrl) {
            throw new Error("Unable to upload the main image.");
          }
          mainImageUrl = uploadedUrl;
        } finally {
          finishUpload();
        }
      }

      const formattedSections = tiptapJSONToSections(tiptapContent);

      const ogPreviews = await uploadOgPreviews(
        blog.imagePreviewDataUrl || blog.imageUrl || mainImageUrl
      );

      const payload = {
        status: blog.status,
        blogCategoryId: blog.blogCategoryIds,
        title: blog.title,
        imageUrl: mainImageUrl,
        miniDescription: blog.miniDescription,
        productId: blog.relatedProductIds,
        sections: formattedSections,
        ogPreviews,
        label: blog.label,
      };

      setBlog((prev) => ({
        ...prev,
        imageUrl: mainImageUrl,
        originalImageUrl: mainImageUrl,
        imageFile: null,
      }));

      const callbacks = {
        success: () => {
          setIsSubmitting(false);
          window.history.go(-1);
        },
        failure: () => {
          setIsSubmitting(false);
        },
      };

      if (blog.id) {
        dispatch(
          updateBlogs({
            payload: { ...payload, id: blog.id },
            callback: callbacks,
          })
        );
      } else {
        dispatch(createBlogs({ payload, callback: callbacks }));
      }
    } catch (error) {
      console.error(error);
      alert(error?.message || "Unable to submit the blog. Please try again.");
      setIsSubmitting(false);
    }
  };

  const generateOgPreviewData = async (source) => {
    if (!source) throw new Error("No base image to build OG previews.");
    const baseImg = await srcToImage(source);
    return OG_SIZES.map(({ w, h }) => {
      const sizeLabel = `${w}x${h}`;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(baseImg, 0, 0, w, h);
      return { size: sizeLabel, dataUrl: canvas.toDataURL("image/jpeg", 0.92) };
    });
  };

  const buildOgPreviews = async () => {
    const source = blog.imagePreviewDataUrl || blog.imageUrl;
    if (!source) {
      alert("Upload the main image first.");
      return;
    }
    try {
      const previews = await generateOgPreviewData(source);
      const existingBySize = new Map(
        ogImages.map((item) => [
          item.size,
          item.originalImageUrl || item.imageUrl || "",
        ])
      );
      setOgImages(
        previews.map((preview) => ({
          ...preview,
          imageUrl: "",
          originalImageUrl: existingBySize.get(preview.size) || "",
        }))
      );
      setOgPanelOpen(true);
    } catch (error) {
      console.error(error);
      alert(
        error?.message ||
          "Unable to build OG previews for the selected image. Please try again."
      );
    }
  };

  const uploadOgPreviews = async (source) => {
    if (!source) return [];

    if (
      !blog.imageFile &&
      ogImages.length > 0 &&
      ogImages.every((preview) => preview.imageUrl)
    ) {
      return ogImages.map((preview) => ({
        size: preview.size,
        imageUrl: preview.imageUrl,
      }));
    }

    let previews = ogImages;
    if (blog.imageFile || ogImages.length === 0) {
      const generated = await generateOgPreviewData(source);
      const existingBySize = new Map(
        ogImages.map((item) => [
          item.size,
          item.originalImageUrl || item.imageUrl || "",
        ])
      );
      previews = generated.map((preview) => ({
        ...preview,
        imageUrl: "",
        originalImageUrl: existingBySize.get(preview.size) || "",
      }));
    }

    const uploadedPreviews = [];
    const updatedState = [];

    for (const preview of previews) {
      if (preview.imageUrl && !blog.imageFile) {
        uploadedPreviews.push({
          size: preview.size,
          imageUrl: preview.imageUrl,
        });
        updatedState.push({ ...preview, originalImageUrl: preview.imageUrl });
        continue;
      }

      const dataUrl = preview.dataUrl || preview.imageUrl;
      if (!dataUrl) {
        throw new Error("Unable to prepare OG preview data for upload.");
      }

      const existingKey = extractS3KeyFromUrl(
        preview.originalImageUrl || preview.imageUrl
      );
      const uniqueSuffix = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const fileName =
        existingKey || `blog-og-${preview.size}-${uniqueSuffix}.jpg`;
      const ogFile = dataURLtoFile(dataUrl, fileName);

      beginUpload();
      try {
        const uploadKey =
          existingKey || `ogPreview-${preview.size}-${uniqueSuffix}`;
        const uploadedUrl = await uploadToS3(
          uploadKey,
          ogFile,
          DESTINATIONS.BLOG_DETAILS,
          false
        );
        if (!uploadedUrl) {
          throw new Error("Unable to upload one of the OG preview images.");
        }
        uploadedPreviews.push({ size: preview.size, imageUrl: uploadedUrl });
        updatedState.push({
          ...preview,
          imageUrl: uploadedUrl,
          originalImageUrl: uploadedUrl,
        });
      } finally {
        finishUpload();
      }
    }

    setOgImages(updatedState);
    return uploadedPreviews;
  };

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  const handleGoBack = () => {
    dispatch(getBlogsByIdResponse(null));
    setBlog(defaultBlog);
    window.history.go(-1);
  };

  const slugify = (text) => {
    if (text) {
      return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    }
    return text;
  };

  return (
    <div className={styles.container}>
      {isNarrow && (
        <button
          className={styles.hamburger}
          aria-label="Toggle navigation"
          onClick={() => setSidebarOpen((p) => !p)}
        >
          <span />
          <span />
          <span />
        </button>
      )}
      <div className={styles.wrapper}>
        <aside
          className={`${styles.sidebar} ${
            isNarrow ? styles.sidebarDrawer : ""
          } ${sidebarOpen ? styles.sidebarOpen : ""}`}
        >
          <div className={styles.logoArea}>
            <IconButton
              onClick={handleGoBack}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "12px",
                backgroundColor: "",
                marginRight: "8px",
              }}
            >
              <KeyboardArrowLeftOutlined />
            </IconButton>
            Kriha&#174; Blog Builder
          </div>

          <div className={styles.sectionLabel}>Editor Tips</div>
          <div className={styles.toolbarGroup}>
            <div
              className={styles.groupLabel}
              style={{ fontSize: "0.72rem", color: "#64748b", lineHeight: 1.5 }}
            >
              • Select text → format toolbar
              <br />
              • Empty line → <strong>+</strong> to insert
              <br />
              • Type <code>- </code> for bullet list
              <br />
              • Type <code>1. </code> for numbered list
              <br />
              • <kbd>Tab</kbd> to indent list items
            </div>
          </div>

          <button
            className={styles.aiPromptButton}
            type="button"
            onClick={() => setAiPromptOpen(true)}
          >
            <span className={styles.aiPromptIcon}>✦</span>
            AI Prompt
          </button>

          <button
            className={styles.toolbarButtonAlt}
            type="button"
            onClick={handleAddRelatedProducts}
          >
            + Add Related Products
          </button>

          <div className={styles.toggleRow}>
            <FormControlLabel
              className={styles.activeToggle}
              control={
                <Switch
                  checked={blog.status === 1}
                  onChange={(e) =>
                    setBlog((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))
                  }
                  size="medium"
                  sx={{ transform: "scale(1.2)" }}
                />
              }
              label="Active"
            />
          </div>

          <button
            className={`${styles.previewToggle} ${
              isPreviewMode ? styles.editToggle : ""
            }`}
            onClick={() => {
              if (!isPreviewMode && tiptapContent) {
                setBlog((p) => ({
                  ...p,
                  sections: tiptapJSONToSections(tiptapContent),
                }));
              }
              setIsPreviewMode((p) => !p);
            }}
          >
            {isPreviewMode ? "Edit Blog" : "Preview Blog"}
          </button>

          <button className={styles.ogButton} onClick={buildOgPreviews}>
            View Open Graph Details
          </button>

          <button
            className={styles.submitSide}
            onClick={handleSubmit}
            disabled={isBusy}
          >
            Submit
          </button>
        </aside>

        <div className={styles.wrapper}>
          {!isPreviewMode && (
            <>
              {!blogFromState?.id && (
                <div className={styles.emptyState}>
                  <p className={styles.emptyTitle}>Start a new blog</p>
                  <p className={styles.emptyHint}>
                    Add the mandatory details first: <b>Blog Category</b>,{" "}
                    <b>Title</b>, <b>Main Image</b>, and <b>Mini Description</b>.
                    Use the left sidebar to add sections.
                  </p>
                </div>
              )}

              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.field}>
                  <label style={{ marginBottom: "8px" }}>
                    Blog Categories *
                  </label>
                  <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(errors.blogCategory)}
                  >
                    <Select
                      labelId="blog-category-label *"
                      multiple
                      placeholder="Select blog categories"
                      value={blog.blogCategoryIds}
                      onChange={(e) => {
                        const value = e.target.value;
                        const nextValue =
                          typeof value === "string" ? value.split(",") : value;
                        setBlog((p) => ({ ...p, blogCategoryIds: nextValue }));
                      }}
                      input={<OutlinedInput />}
                      renderValue={(selected) =>
                        selected
                          .map((id) => {
                            const category = activeBlogCategories?.find(
                              (cat) => cat?.id === id
                            );
                            return category?.name || `Category ${id}`;
                          })
                          .join(", ")
                      }
                    >
                      {activeBlogCategories?.map((category) => (
                        <MenuItem key={category?.id} value={category?.id}>
                          <Checkbox
                            checked={
                              blog.blogCategoryIds.indexOf(category?.id) > -1
                            }
                          />
                          <ListItemText primary={category?.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {blog.blogCategoryIds.length > 0 && (
                    <div className={styles.selectedCategories}>
                      {blog.blogCategoryIds.map((id) => {
                        const category = activeBlogCategories?.find(
                          (cat) => cat?.id === id
                        );
                        return (
                          <span key={id} className={styles.categoryChip}>
                            <span className={styles.categoryChipLabel}>
                              {category?.name || `Category ${id}`}
                            </span>
                            <button
                              type="button"
                              className={styles.chipRemoveDanger}
                              onClick={() =>
                                setBlog((p) => ({
                                  ...p,
                                  blogCategoryIds: p.blogCategoryIds.filter(
                                    (cid) => cid !== id
                                  ),
                                }))
                              }
                              aria-label={`Remove ${category?.name || id}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {errors.blogCategory && (
                    <span className={styles.error}>{errors.blogCategory}</span>
                  )}
                </div>

                <div className={styles.field}>
                  <label>Title *</label>
                  <input
                    type="text"
                    placeholder="Add a Title"
                    value={blog.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      const label = slugify(title);

                      setBlog((p) => ({
                        ...p,
                        title,
                        label,
                      }));
                    }}
                  />
                  {errors.title && (
                    <span className={styles.error}>{errors.title}</span>
                  )}
                </div>

                <div className={styles.field}>
                  <label>Label </label>
                  <input
                    type="text"
                    placeholder="Add a Title"
                    value={blog.label}
                    disabled
                  />
                </div>

                <div className={styles.field}>
                  <label>
                    Main Image (kept at {DEFAULT_ASPECT.toFixed(2)} aspect){" "}
                    <span className={styles.helper}>
                      (crop before upload) *
                    </span>
                  </label>
                  <CropImageUpload
                    aspect={DEFAULT_ASPECT}
                    cropSize={MAIN_CROP_SIZE}
                    onCropped={({ dataUrl, file }) => {
                      setBlog((p) => ({
                        ...p,
                        imagePreviewDataUrl: dataUrl,
                        imageUrl: "",
                        imageFile: file,
                      }));
                      setOgImages([]);
                      setOgPanelOpen(false);
                    }}
                    onClear={() => {
                      setOgImages([]);
                      setOgPanelOpen(false);
                      setBlog((p) => ({
                        ...p,
                        imagePreviewDataUrl: "",
                        imageUrl: "",
                        imageFile: null,
                      }));
                    }}
                  />
                  {errors.image && (
                    <span className={styles.error}>{errors.image}</span>
                  )}

                  {(blog.imagePreviewDataUrl || blog.imageUrl) && (
                    <div className={styles.heroWrap}>
                      <img
                        className={styles.heroImage}
                        src={blog.imagePreviewDataUrl || blog.imageUrl}
                        alt="Main blog image"
                      />
                    </div>
                  )}
                </div>

                <div className={styles.field}>
                  <label>Mini Description *</label>
                  <textarea
                    rows={3}
                    placeholder="Short summary that appears under the title…"
                    value={blog.miniDescription}
                    onChange={(e) =>
                      setBlog((p) => ({
                        ...p,
                        miniDescription: e.target.value,
                      }))
                    }
                  />
                  {errors.miniDescription && (
                    <span className={styles.error}>
                      {errors.miniDescription}
                    </span>
                  )}
                </div>

                <div className={styles.field}>
                  <label>Related Products</label>
                  {selectedRelatedProducts.length > 0 ? (
                    <div className={styles.selectedProducts}>
                      {selectedRelatedProducts.map((product) => (
                        <button
                          type="button"
                          key={product?.id}
                          className={styles.productChip}
                          onClick={() => toggleRelatedProduct(product?.id)}
                        >
                          <span className={styles.productChipLabel}>
                            {product?.name ||
                              product?.title ||
                              product?.productName ||
                              `Product ${product?.id}`}
                          </span>
                          <span className={styles.chipRemove} aria-hidden>
                            ×
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className={styles.helper}>
                      No related products selected yet.
                    </span>
                  )}
                  <button
                    type="button"
                    className={styles.manageRelatedButton}
                    onClick={handleAddRelatedProducts}
                  >
                    Manage Related Products
                  </button>
                </div>

                <div className={styles.field}>
                  <label>Blog Content</label>
                  <RichTextEditor
                    content={tiptapContent}
                    onChange={setTiptapContent}
                    uploadToS3={uploadToS3}
                    beginUpload={beginUpload}
                    finishUpload={finishUpload}
                  />
                </div>

                <div className={styles.actions}>
                  <button
                    type="submit"
                    className={styles.ctaButton}
                    disabled={isBusy}
                  >
                    Submit
                  </button>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => {
                      setBlog({ ...defaultBlog });
                      setTiptapContent(null);
                      setErrors({});
                      setOgImages([]);
                      setOgPanelOpen(false);
                      setIsPreviewMode(false);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </form>
            </>
          )}

          {isPreviewMode && (
            <div className={styles.previewOnly}>
              <PreviewBlog
                blog={
                  tiptapContent
                    ? { ...blog, sections: tiptapJSONToSections(tiptapContent) }
                    : blog
                }
                categoryPreviewLabels={categoryPreviewLabel}
              />
            </div>
          )}
        </div>

        {ogPanelOpen && (
          <div className={styles.ogPanel}>
            <div className={styles.ogHeader}>
              <span>Open Graph Previews</span>
              <button type="button" onClick={() => setOgPanelOpen(false)}>
                Close
              </button>
            </div>
            <div className={styles.ogGrid}>
              {ogImages.map((o) => (
                <div className={styles.ogItem} key={o.size}>
                  <div className={styles.ogSize}>{o.size}</div>
                  <img src={o.imageUrl || o.dataUrl} alt={`OG ${o.size}`} />
                  <a
                    className={styles.downloadLink}
                    href={o.imageUrl || o.dataUrl}
                    download={`og-${o.size}.jpg`}
                  >
                    Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        <AIPromptDialog
          open={aiPromptOpen}
          onClose={() => setAiPromptOpen(false)}
          onImport={(tiptapJson) => {
            setTiptapContent(tiptapJson);
            setIsPreviewMode(false);
          }}
        />

        <Dialog
          fullWidth
          maxWidth="md"
          open={relatedProductsOpen}
          onClose={() => setRelatedProductsOpen(false)}
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Select Related Products
            <IconButton
              aria-label="Close"
              onClick={() => setRelatedProductsOpen(false)}
              sx={{ marginLeft: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <RelatedProductsSearch
              products={products}
              selectedProductIds={blog.relatedProductIds}
              onToggle={toggleRelatedProduct}
              onSave={handleRelatedProductsSave}
            />
          </DialogContent>
        </Dialog>

        {isBusy && (
          <div className={styles.loadingOverlay}>
            <ClipLoader color="#0a6152" size={48} />
          </div>
        )}
      </div>
      {isNarrow && sidebarOpen && (
        <div
          className={styles.sidebarBackdrop}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    products: state?.product?.products,
    activeBlogCategories: state?.blogcategory?.categories,
    blogFromState: state?.blogs?.blog,
  };
};

export default connect(mapStateToProps, null)(ManageBlog);
